/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useDoSeeDataContext } from "../../../context/doSeeData/DoSeeDataContext";
import { pushOneHotel } from "../../../middlewares/sysNavigation/SysNavigation";
import { BreadCrumbComponent } from "../../../middlewares/breadCrumb/BreadCrumbComponent";
import {
  Modal,
  GoBack,
  MyButton,
  MySearch,
  Pagination,
  DashboardIntro,
} from "../../home/component/Component";
import { _TYPES_ } from "../utils/types";
import { _PAGE_SIZE_ } from "../../../middlewares/other/tools";
import { useGeoPositionContext } from "../utils/geoPosition";
import { useAdminContext } from "../../../context/admin/context/AdminContext";
import PromoteContent from "../utils/PromoteContent";
import { FaRegTrashAlt } from "react-icons/fa";
import { RiRefreshLine } from "react-icons/ri";

const addRoute = "/data/hotel/add";
const editRoute = "/data/hotel/edit";
const doseeDataRoutes = "/dosee/data";

const Overview = () => {
  const [hotelsList, setHotelsList] = useState([]);
  const { hotels, hotelsCount, fetchDoSeeData, deleteOneContent } =
    useDoSeeDataContext();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(_PAGE_SIZE_);

  // Search term (string) for local filtering
  const [localSearch, setLocalSearch] = useState("");

  const [loading, setLoading] = useState(false);
  const [selectedContentIds, setSelectedContentIds] = useState([]);

  const {
    country_id,
    entry_label,
    first_level_id,
    fourth_level_id,
    second_level_id,
    third_level_id,
  } = useGeoPositionContext();

  const data_request_body = {
    country_id,
    first_level_id,
    second_level_id,
    third_level_id,
    fourth_level_id,
    content: _TYPES_.HOTEL,
  };

  // Fetch whenever location filters/page/pageSize change (and on first mount)
  useEffect(() => {
    fetchDoSeeData(data_request_body, page, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country_id, first_level_id, second_level_id, third_level_id, fourth_level_id, page, pageSize]);

  // Keep local list in sync with context data
  useEffect(() => {
    setHotelsList(Array.isArray(hotels) ? hotels : []);
  }, [hotels]);

  const reloadData = () => fetchDoSeeData(data_request_body, page, pageSize);

  // Local filtering (by title)
  const LocalData = useMemo(() => {
    if (!localSearch) return hotelsList || [];
    const key = localSearch.toString().toLowerCase().trim();
    return (hotelsList || []).filter(
      (htl) => htl?.title?.toString().toLowerCase().includes(key)
    );
  }, [hotelsList, localSearch]);

  // --- Selection logic (robust, page-aware, indeterminate) ---
  const visibleIds = useMemo(
    () => (Array.isArray(LocalData) ? LocalData.map((h) => h?.hotel_id).filter(Boolean) : []),
    [LocalData]
  );
  const selectedSet = useMemo(() => new Set(selectedContentIds), [selectedContentIds]);
  const isAllSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedSet.has(id));
  const isIndeterminate = selectedSet.size > 0 && !isAllSelected && visibleIds.some((id) => selectedSet.has(id));
  const selectAllRef = useRef(null);

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = isIndeterminate;
    }
  }, [isIndeterminate]);

  const toggleSelectAll = () => {
    if (isAllSelected) {
      // Deselect only the visible items
      const next = selectedContentIds.filter((id) => !visibleIds.includes(id));
      setSelectedContentIds(next);
    } else {
      // Add all visible items (deduped)
      const next = Array.from(new Set([...selectedContentIds, ...visibleIds]));
      setSelectedContentIds(next);
    }
  };

  const toggleOne = (id) => {
    setSelectedContentIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const clearSelection = () => setSelectedContentIds([]);

  const deleteMany = async () => {
    if (!selectedContentIds.length) return;
    setLoading(true);
    try {
      for (let i = 0; i < selectedContentIds.length; i++) {
        // eslint-disable-next-line no-await-in-loop
        await deleteOneContent({ data_id: selectedContentIds[i], content: _TYPES_.HOTEL });
      }
      clearSelection();
      reloadData();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-3 lg:p-6 h-full overflow-y-auto bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <BreadCrumbComponent />
        <div className="flex items-center justify-between mt-2">
          <GoBack props={{ route: doseeDataRoutes }} />
          {country_id ? (
            <Link to={addRoute}>
              <MyButton
                title="Add Hotel"
                className="py-3 px-6 rounded-xl bg-blue-700 hover:bg-blue-600 text-white shadow-sm"
                onClick={() => { }}
              />
            </Link>
          ) : <div />}
        </div>

        <div className="my-4">
          <DashboardIntro props={{ intro: `Hotels`, name: entry_label }} />
        </div>

        {/* Controls Card */}
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-3 lg:p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="w-full sm:max-w-md">
              <MySearch
                type={_TYPES_.HOTEL}
                value={localSearch}
                showGlobalSearch={true}
                setValue={setLocalSearch}
                onChange={(e) => setLocalSearch(e.target.value)}
              />
            </div>

            {/* Selection Toolbar */}
            <div className="flex flex-wrap items-center gap-2">
              <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700">
                <input
                  ref={selectAllRef}
                  type="checkbox"
                  checked={isAllSelected}
                  onChange={toggleSelectAll}
                  className="h-4 w-4 rounded border-gray-300 focus:ring-2 focus:ring-blue-500"
                />
                <span>
                  {isAllSelected ? "Unselect all (visible)" : isIndeterminate ? "Select remaining (visible)" : "Select all (visible)"}
                </span>
              </label>

              <MyButton
                onClick={deleteMany}
                disabled={!selectedContentIds.length || loading}
                className={`flex items-center gap-2 py-2 px-4 rounded-lg border ${selectedContentIds.length
                    ? "bg-red-50 hover:bg-red-100 border-red-200 text-red-700"
                    : "bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed"
                  }`}
                title={"Delete Selected"}
              >
                {loading ? (
                  <RiRefreshLine className="text-base animate-spin" />
                ) : (
                  <FaRegTrashAlt className="text-base" />
                )}
                <span>Delete {selectedContentIds.length || 0}</span>
              </MyButton>

              {!!selectedContentIds.length && (
                <button
                  type="button"
                  onClick={clearSelection}
                  className="text-sm text-gray-600 hover:text-gray-900 underline"
                >
                  Clear selection
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Data Table Card */}
        <div className="mt-4 bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr className="text-left text-gray-600">
                  <th className="px-4 py-3 w-12">
                    <input
                      ref={selectAllRef}
                      type="checkbox"
                      checked={isAllSelected}
                      onChange={toggleSelectAll}
                      className="h-4 w-4 rounded border-gray-300 focus:ring-2 focus:ring-blue-500"
                      aria-label="Select all visible"
                    />
                  </th>
                  <th className="px-4 py-3 font-semibold">Title</th>
                  <th className="px-4 py-3 font-semibold">Slug</th>
                  <th className="px-4 py-3 text-center font-semibold">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {Array.isArray(LocalData) && LocalData.length > 0 ? (
                  LocalData.map((htl) => {
                    const id = htl?.hotel_id;
                    const checked = selectedSet.has(id);
                    return (
                      <tr
                        key={id}
                        className="odd:bg-white even:bg-gray-50 hover:bg-gray-100/70 transition-colors"
                      >
                        <td className="px-4 py-3 w-12">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleOne(id)}
                            className="h-4 w-4 rounded border-gray-300 focus:ring-2 focus:ring-blue-500"
                            aria-label={`Select ${htl?.title ?? "hotel"}`}
                          />
                        </td>
                        <td className="px-4 py-3 align-middle text-gray-900">{htl?.title}</td>
                        <td className="px-4 py-3 align-middle text-gray-600">{htl?.slug}</td>
                        <td className="px-2 py-2 align-middle">
                          <ActionButton props={htl} reloadData={reloadData} />
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={4} className="px-4 py-10 text-center text-gray-500">
                      No hotels found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-4">
          <Pagination
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            total_items={hotelsCount}
            callbackFunction={(p, size) => {
              fetchDoSeeData(data_request_body, p, size);
            }}
          />
        </div>
      </div>
    </div>
  );
};

/**
 *  @author Ntavigwa Bashombe
 * @param {*} props
 * @returns
 */
const ActionButton = ({ props, reloadData }) => {
  const { sysAdmin } = useAdminContext();
  const isSuperAdmin = ["super_admin", "admin"].includes(sysAdmin?.role);
  const { hotels, deleteOneContent, updateOneContent } = useDoSeeDataContext();

  const [showModal, setShowModal] = useState(false);

  const [promote, setPromote] = useState(false);
  const [promoteLoader, setPromoteLoader] = useState(false);

  const hotel_id = props.hotel_id;
  const hotel_title = props.title;
  const hotel = props;

  const deleteOneHotelById = async (hotel_id) => {
    const { status } = await deleteOneContent({
      data_id: hotel_id,
      content: _TYPES_.HOTEL,
    });

    setShowModal(false);

    if (status === 200) {
      reloadData();
    }
  };

  const buildData = (object, position) => {
    return {
      promoted: `${position}`,
      country_id: object?.country_id,
      description: object?.description,
      first_level_id: object?.first_level_id,
      fourth_level_id: object?.fourth_level_id,
      second_level_id: object?.second_level_id,
      slug: object?.slug,
      status: object?.status,
      third_level_id: object?.third_level_id,
      thumbnail: object?.thumbnail,
      title: object?.title,
    };
  };

  /**
   * @author Ntavigwa Bashombe
   * @param {*} hotel
   */
  const promotion = async (position) => {
    const previousPromoted = hotels?.find(
      (hotel) => hotel?.promoted === String(position)
    );

    setPromoteLoader(true);

    const { status } = await updateOneContent({
      data_id: hotel?.hotel_id,
      data: buildData(props, position),
      content: _TYPES_.HOTEL,
    });

    if (previousPromoted) {
      await updateOneContent({
        data_id: previousPromoted?.hotel_id,
        data: buildData(previousPromoted, "no"),
        content: _TYPES_.HOTEL,
      });
    }

    if (status === 200) {
      reloadData();
    }

    setPromoteLoader(false);
  };

  const suspension = async (hotel) => {
    const data = {
      country_id: hotel?.country_id,
      description: hotel?.description,
      first_level_id: hotel?.first_level_id,
      fourth_level_id: hotel?.fourth_level_id,
      second_level_id: hotel?.second_level_id,
      slug: hotel?.slug,
      status: hotel?.status === 1 ? 0 : 1,
      third_level_id: hotel?.third_level_id,
      thumbnail: hotel?.thumbnail,
      title: hotel?.title,
    };

    const { status } = await updateOneContent({
      data_id: hotel?.hotel_id,
      data: data,
      content: _TYPES_.HOTEL,
    });

    if (status === 200) {
      reloadData();
    }
  };

  return (
    <>
      <Modal
        props={{
          name: hotel_title,
          showModal: showModal,
          setShowModal: () => setShowModal(false),
          delete: () => deleteOneHotelById(hotel_id),
        }}
      />

      <PromoteContent
        visible={promote}
        setVisible={setPromote}
        loading={promoteLoader}
        ids={hotels
          ?.filter(
            (hotel) => !["yes", "no", null, undefined].includes(hotel?.promoted)
          )
          .map((hotel) => ({
            id: hotel?.hotel_id,
            title: hotel?.title,
            position: parseInt(hotel?.promoted),
          }))}
        onClick={(position) => promotion(position)}
      />

      <div className="flex flex-row justify-end items-center px-2 py-2 gap-2 text-xs">
        {isSuperAdmin ? (
          <>
            <MyButton
              title={hotel?.status === 1 ? "Suspend" : "Unsuspend"}
              className={`py-2 px-4 rounded-lg ${hotel?.status === 1
                  ? "bg-gray-900 hover:bg-gray-700 text-white"
                  : "bg-orange-600 hover:bg-orange-500 text-white"
                }`}
              onClick={() => suspension(hotel)}
            />
            <MyButton
              title={
                ["yes", "no", null, undefined].includes(hotel?.promoted)
                  ? "Promote"
                  : "Unpromote"
              }
              className={`py-2 px-4 rounded-lg ${["yes", "no", null, undefined].includes(hotel?.promoted)
                  ? "bg-gray-900 hover:bg-gray-700 text-white"
                  : "bg-orange-600 hover:bg-orange-500 text-white"
                }`}
              onClick={() => setPromote(true)}
            />
          </>
        ) : null}
        <Link to={editRoute}>
          <MyButton
            title="Edit"
            className={`py-2 px-4 rounded-lg bg-blue-700 hover:bg-blue-600 text-white`}
            onClick={() => pushOneHotel(hotel)}
          />
        </Link>
        <MyButton
          title="Delete"
          className={`py-2 px-4 rounded-lg bg-red-700 hover:bg-red-600 text-white`}
          onClick={() => setShowModal(true)}
        />
      </div>
    </>
  );
};

export default Overview;